import { SDK_FUNCTIONALITY_ADS, SDKFunctionalityName } from '@crazygames/game-types/src/sdk.type';
import {
  PortalQAToolResult,
  QAResult,
  FullRequirementKey,
  BasicRequirementKey,
  RequirementsChecklist,
  FullRequirements,
  BasicRequirements,
  basicRequirementsItems,
  fullRequirementsItems,
} from '@crazygames/game-types/src/submission/qa-results/qa-result.type';
import { MultiplayerOptions } from '../../Submission/GameBaseForm/baseFormHelper';
import { GameBuild } from '../../../Admin/Game/gql/game-build-graphql';

/**
 * Required fields that need to exist on submission objects passed to createDefaultRequirements
 */
type RequiredSubmissionFields = {
  isFullLaunch: boolean;
  multiplayerOptions?: MultiplayerOptions;
  mobileFriendly: boolean;
};

export function portalQaDataToQaResult(
  qaData: PortalQAToolResult,
  adScenario: string,
  manualChecks: RequirementsChecklist,
  isFullLaunch: boolean,
): QAResult {
  const { detectedSDKFunctionality } = qaData!;

  const basicRequirements = {} as BasicRequirements;
  basicRequirementsItems.forEach((item) => {
    const key = item.key as BasicRequirementKey;

    if (manualChecks[key] !== undefined) {
      basicRequirements[key] = manualChecks[key];
    } else if (item.type === 'automatic' && item.validationFunction) {
      try {
        basicRequirements[key] = item.validationFunction(qaData);
      } catch (_) {
        basicRequirements[key] = null;
      }
    } else {
      basicRequirements[key] = null;
    }
  });

  const fullRequirements = {} as FullRequirements;
  fullRequirementsItems.forEach((item) => {
    const key = item.key as FullRequirementKey;

    if (!isFullLaunch) {
      fullRequirements[key] = null;
      return;
    }

    if (manualChecks[key] !== undefined) {
      fullRequirements[key] = manualChecks[key];
    } else if (item.type === 'automatic' && item.validationFunction) {
      try {
        fullRequirements[key] = item.validationFunction(qaData);
      } catch (_) {
        fullRequirements[key] = null;
      }
    } else {
      fullRequirements[key] = null;
    }
  });

  const qaResult: QAResult = {
    SDKImplemented: detectedSDKFunctionality.length > 0,
    SDKInfo: qaData.SDKInfo,
    adScenario,
    initialLoadSizeBytes: qaData.initialLoadSizeBytes ?? undefined,
    loadTimeSeconds: qaData.loadTimeSeconds ?? undefined,
    detectedSDKFunctionality,
    basicRequirements,
    fullRequirements,
    sdkLoadedFromUs: qaData.sdkLoadedFromUs ?? null,
  };

  return qaResult;
}

export const createDefaultBasicRequirements = (): Record<BasicRequirementKey, boolean | null> => {
  return basicRequirementsItems.reduce(
    (acc, item) => {
      acc[item.key as BasicRequirementKey] = null;
      return acc;
    },
    {} as Record<BasicRequirementKey, boolean | null>,
  );
};

export const createDefaultFullRequirements = (): Record<FullRequirementKey, boolean | null> => {
  return fullRequirementsItems.reduce(
    (acc, item) => {
      acc[item.key as FullRequirementKey] = null;
      return acc;
    },
    {} as Record<FullRequirementKey, boolean | null>,
  );
};

export const createDefaultRequirements = <T extends RequiredSubmissionFields>(
  submission: T,
  gameBuild: GameBuild,
  sdkInitialized: boolean,
  detectedSDKFunctionality?: SDKFunctionalityName[],
): RequirementsChecklist => {
  const checklist: Partial<RequirementsChecklist> = {};

  basicRequirementsItems.forEach((item) => {
    checklist[item.key] = null;
  });

  if (submission.isFullLaunch) {
    fullRequirementsItems.forEach((item) => {
      checklist[item.key] = null;
    });
  }

  // remove not applicable requirements
  // TODO this filtering can be done as a method "isEnabled" for each requirement, however at the moment this would required moving too many types in the shared packages
  if (!gameBuild.hasIap) {
    const iapRequirements = [...basicRequirementsItems, ...fullRequirementsItems].filter((item) => item.group === 'iap');
    iapRequirements.forEach((item) => {
      delete checklist[item.key];
    });
  }
  if (!submission.multiplayerOptions?.isMultiplayer) {
    const multiplayerRequirements = [...basicRequirementsItems, ...fullRequirementsItems].filter((item) => item.group === 'multiplayer');
    multiplayerRequirements.forEach((item) => {
      delete checklist[item.key];
    });
  }

  // only show for submissions using their own backend
  if (gameBuild.apsConfig.progressType !== 'INGAMEAUTOLOGIN') {
    delete checklist['automaticLogicCzyUsers'];
  }

  if (!sdkInitialized) {
    delete checklist['gameplayStart'];
    delete checklist['firstGameplayStart'];
  }

  if (!submission.mobileFriendly) {
    delete checklist['mobileFriendly'];
  }

  // if no ads event is detected, remove all ads checks
  const detectedSDKFunctionalitySet = new Set(detectedSDKFunctionality);
  if (detectedSDKFunctionality && SDK_FUNCTIONALITY_ADS.every((e) => !detectedSDKFunctionalitySet.has(e as SDKFunctionalityName))) {
    delete checklist['adheresToAdsRequirements'];
    delete checklist['adsPlacedCorrectly'];
    delete checklist['midgameAd'];
    delete checklist['rewardedAd'];
    delete checklist['banner'];
    delete checklist['adBlockSupported'];
  }

  if (gameBuild.loaderType === 'iframe') {
    delete checklist['initialDownloadSize'];
  } else {
    delete checklist['initialLoadTime'];
  }

  return checklist as RequirementsChecklist;
};
